import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@clari/ui'
import { ErrorBanner } from '@/components/ErrorBanner'
import { apiErrorMessage } from '@/lib/api-error'
import { adminApi } from '@/lib/admin-api'
import { formatDateTime } from '@/lib/format'
import type { AdminRiskAssessmentDto } from '@/lib/types'

function scoreClass(score: number): string {
  if (score >= 75) return 'text-danger'
  if (score >= 50) return 'text-warning'
  return 'text-text-muted'
}

/**
 * One escalated pre-flight assessment (USP #2). Flags/score are ops-only —
 * the customer never sees them (tipping-off). A decision requires a written
 * reason, which lands in audit_logs.
 */
function RiskRow({
  item,
  onResolve,
}: {
  item: AdminRiskAssessmentDto
  onResolve: (decision: 'approved' | 'rejected', reason: string) => Promise<void>
}) {
  const { t, i18n } = useTranslation('admin')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  async function decide(decision: 'approved' | 'rejected') {
    setBusy(true)
    try {
      await onResolve(decision, reason.trim())
    } finally {
      setBusy(false)
    }
  }

  const ownerName = [item.user.first_name, item.user.last_name].filter(Boolean).join(' ')
  const reasonReady = reason.trim().length > 0

  return (
    <div className="rounded-lg border border-border p-4">
      <div className="flex items-center justify-between gap-3">
        <Link to={`/users/${item.user.id}`} className="font-mono text-sm hover:underline">
          {item.user.email}
        </Link>
        <span className={`shrink-0 text-sm font-semibold ${scoreClass(item.risk_score)}`}>
          {t('risk.score', { score: item.risk_score })}
        </span>
      </div>
      {ownerName ? <p className="text-xs text-text-muted">{ownerName}</p> : null}

      {item.flags.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {item.flags.map((f) => (
            <span key={f} className="rounded bg-bg-subtle px-2 py-0.5 text-xs text-text-muted">
              {t(`risk.flags.${f}`, { defaultValue: f })}
            </span>
          ))}
        </div>
      ) : null}

      {item.justification_provided ? (
        <p className="mt-2 text-sm">
          <span className="text-text-muted">{t('risk.justification')}: </span>
          {item.justification_provided}
        </p>
      ) : null}
      {item.justification_doc_url ? (
        <a
          href={item.justification_doc_url}
          target="_blank"
          rel="noreferrer"
          className="mt-1 inline-block text-xs text-accent-warm hover:underline"
        >
          {t('risk.view_doc')}
        </a>
      ) : null}

      <p className="mt-1 text-xs text-text-subtle">
        {t('risk.created')} {formatDateTime(item.created_at, i18n.language)}
      </p>

      <input
        type="text"
        className="clari-input mt-3 w-full"
        placeholder={t('risk.reason_placeholder')}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        aria-label={t('risk.reason_placeholder')}
      />
      <div className="mt-2 flex gap-2">
        <Button size="sm" disabled={busy || !reasonReady} onClick={() => decide('approved')}>
          {t('risk.approve')}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy || !reasonReady}
          onClick={() => decide('rejected')}
        >
          {t('risk.reject')}
        </Button>
      </div>
    </div>
  )
}

export function RiskAssessments() {
  const { t } = useTranslation('admin')
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [actionError, setActionError] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['admin-risk-assessments', page],
    queryFn: () => adminApi.listRiskAssessments(page),
  })
  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / query.data.pageSize)) : 1

  async function resolve(id: string, decision: 'approved' | 'rejected', reason: string) {
    setActionError(null)
    try {
      await adminApi.resolveRiskAssessment(id, decision, reason)
      await qc.invalidateQueries({ queryKey: ['admin-risk-assessments'] })
    } catch (e) {
      setActionError(apiErrorMessage(e))
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">{t('risk.title')}</h1>
      <p className="text-text-muted">{t('risk.subtitle')}</p>

      {query.isError ? <ErrorBanner message={apiErrorMessage(query.error)} /> : null}
      {actionError ? <ErrorBanner message={actionError} /> : null}
      {query.isLoading ? <p className="text-text-muted">{t('common.loading')}</p> : null}

      {query.data && query.data.items.length === 0 ? (
        <p className="text-text-muted">{t('risk.empty')}</p>
      ) : null}

      <div className="flex flex-col gap-3">
        {query.data?.items.map((item) => (
          <RiskRow key={item.id} item={item} onResolve={(d, r) => resolve(item.id, d, r)} />
        ))}
      </div>

      <div className="flex items-center gap-3 text-sm">
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          {t('common.prev')}
        </button>
        <span className="text-text-muted">{t('common.page', { page })}</span>
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          {t('common.next')}
        </button>
      </div>
    </div>
  )
}
